/**
 * Pure helpers that compute quota-banner visibility from the user object
 * returned by GET /api/auth/user.  Extracted so they can be tested without
 * a DOM environment.
 *
 * IMPORTANT — no memoisation: every helper re-derives its boolean on every
 * call from the user object passed in.  The user query has refetchOnWindowFocus
 * enabled, so switching back to the tab after Stripe checkout triggers an
 * immediate re-fetch; the ThankYouPage also invalidates the query on mount so
 * subscription status is fresh before the redirect to /dashboard.  When a user
 * upgrades mid-session the refreshed user object flows straight through these
 * functions on the next render and the banners disappear automatically — no
 * manual cache invalidation is needed at the call site.
 * Do NOT wrap calls to these helpers in useMemo/useCallback; doing so would
 * risk serving a stale banner state after the user object has been refreshed.
 */

import { QUOTA_ERROR_CODES, TRIAL_QUOTA_TOTAL, type QuotaErrorCode } from "@shared/quota-constants";
export { QUOTA_ERROR_CODES, TRIAL_QUOTA_TOTAL, type QuotaErrorCode } from "@shared/quota-constants";

/**
 * Returns true when the given error code is one of the three quota-limit
 * codes that should trigger the quota-exceeded banner on the Auto Quote page.
 */
export function isQuotaErrorCode(code: unknown): code is QuotaErrorCode {
  return QUOTA_ERROR_CODES.includes(code as QuotaErrorCode);
}

export interface QuotaUser {
  quotaLimitEnforced?: boolean | null;
  trialEndAt?: Date | string | null;
  subscriptionStatus?: string | null;
  trialQuotaExceeded?: boolean | null;
  monthlyQuotaExceeded?: boolean | null;
  trialQuotesUsed?: number | null;
  monthlyQuotesUsed?: number | null;
  monthlyQuotaLimit?: number | null;
  bonusQuotaCredits?: number | null;
  monthlyQuotaResetAt?: string | null;
}

/**
 * True when the user is still inside their free trial period AND the
 * server has already flagged the trial quota as enforced
 * (`quotaLimitEnforced`) or the user has reached the trial quote allowance
 * (`trialQuotaExceeded`).
 *
 * Matches the `isTrialQuotaEnforced` derivation used in all three
 * quote pages (Manual, Template, Auto).
 */
export function isTrialQuotaEnforced(user: QuotaUser | null | undefined): boolean {
  return !!(
    user?.quotaLimitEnforced &&
    user?.trialEndAt &&
    new Date(user.trialEndAt) > new Date() &&
    user?.subscriptionStatus !== "active"
  );
}

/**
 * True when the user must be blocked from creating new quotes due to
 * trial quota being hit.  Shows the amber "trial limit reached" banner
 * and hides the form on Manual and Template pages.
 *
 * Matches: `const trialBlocked = isTrialQuotaEnforced || !!user?.trialQuotaExceeded`
 */
export function isTrialBlocked(user: QuotaUser | null | undefined): boolean {
  return isTrialQuotaEnforced(user) || !!user?.trialQuotaExceeded;
}

/**
 * True when a paid subscriber has exhausted their monthly AI-quote
 * allowance AND has no bonus credits remaining.  Shows the amber info
 * card but leaves the form accessible (Manual and Template quotes are
 * unaffected by the monthly counter).
 *
 * Two-layer guard:
 *  1. Server flag: `monthlyQuotaExceeded` is only set true when both
 *     monthly allowance and bonus credits are exhausted.
 *  2. Client-side bonus guard: explicitly returns false when bonus credits
 *     remain, regardless of the server flag — reduces coupling to backend
 *     flag derivation and is safe to apply redundantly.
 */
export function isMonthlyLimitReached(user: QuotaUser | null | undefined): boolean {
  if (!user?.monthlyQuotaExceeded) return false;
  const bonusCredits = user.bonusQuotaCredits ?? 0;
  return bonusCredits === 0;
}

/**
 * True when the user is on an active free trial and `trialQuotesUsed` is
 * populated on the user object.  Only true while subscriptionStatus !== "active".
 */
export function isOnTrial(user: QuotaUser | null | undefined): boolean {
  return !!(
    user?.trialEndAt &&
    new Date(user.trialEndAt) > new Date() &&
    user?.subscriptionStatus !== "active" &&
    user?.trialQuotesUsed !== undefined &&
    user?.trialQuotesUsed !== null
  );
}

/**
 * Returns the number of whole days remaining in the trial (0 = expires today,
 * null = not on trial or no end date).  Uses Math.ceil so a trial ending in
 * less than 24 hours still shows "1 day left" rather than "0 days left".
 */
export function getTrialDaysLeft(user: QuotaUser | null | undefined): number | null {
  if (!isOnTrial(user)) return null;
  const msLeft = new Date(user!.trialEndAt!).getTime() - Date.now();
  return Math.max(0, Math.ceil(msLeft / (1000 * 60 * 60 * 24)));
}

/** Shape returned by getQuotaInfo. */
export interface QuotaInfo {
  quotesUsed: number;
  quotesTotal: number;
  quotesLeft: number;
  /** 'trial' = within free trial; 'monthly' = active paid plan with a monthly cap */
  period: "trial" | "monthly";
}

/**
 * Returns quota usage data suitable for rendering a counter or progress bar,
 * or null when there is no applicable quota (admin, unlimited plan, or data
 * not yet available on the user object).
 *
 * - Trial users: derived from trialQuotesUsed / TRIAL_QUOTA_TOTAL
 * - Paid subscribers: derived from monthlyQuotesUsed / monthlyQuotaLimit
 *   (both fields are set by the API only when the plan has a monthly cap)
 */
export function getQuotaInfo(user: QuotaUser | null | undefined): QuotaInfo | null {
  if (!user) return null;

  const isTrial =
    user.trialEndAt &&
    new Date(user.trialEndAt) > new Date() &&
    user.subscriptionStatus !== "active";

  if (isTrial) {
    if (user.trialQuotesUsed === undefined || user.trialQuotesUsed === null) return null;
    const bonusCredits = user.bonusQuotaCredits ?? 0;
    const quotesUsed = user.trialQuotesUsed;
    const quotesTotal = TRIAL_QUOTA_TOTAL + bonusCredits;
    const quotesLeft = Math.max(0, TRIAL_QUOTA_TOTAL - quotesUsed) + bonusCredits;
    return { quotesUsed, quotesTotal, quotesLeft, period: "trial" };
  }

  if (
    user.subscriptionStatus === "active" &&
    user.monthlyQuotaLimit !== undefined &&
    user.monthlyQuotaLimit !== null &&
    user.monthlyQuotesUsed !== undefined &&
    user.monthlyQuotesUsed !== null
  ) {
    const bonusCredits = user.bonusQuotaCredits ?? 0;
    const quotesUsed = user.monthlyQuotesUsed;
    const planLimit = user.monthlyQuotaLimit;
    const quotesTotal = planLimit + bonusCredits;
    const quotesLeft = Math.max(0, planLimit - quotesUsed) + bonusCredits;
    return { quotesUsed, quotesTotal, quotesLeft, period: "monthly" };
  }

  return null;
}
